import { NavigationCancel, NavigationCancellationCode, NavigationExtras, Router } from '@angular/router';

/**
 * Result of {@link navigateHandingOff}: `navigated` when the navigation completed (a guard's UrlTree
 * redirect counts: the router resolves it `true` at the redirect target), `handedOff` when a guard started
 * another navigation itself and returned false (PermissionGuard sending the user to /unauthorized),
 * `rejected` when the navigation simply did not happen (a guard returned false and went nowhere).
 */
export type NavigationOutcome = 'navigated' | 'handedOff' | 'rejected';

/**
 * `router.navigate` that tells a guard redirect apart from a plain rejection. Both resolve `false`, but a
 * caller that falls back somewhere on `false` must not do so after a redirect: its fallback navigation
 * would supersede the redirect and hide the reason (a denied deep link would land on home instead of
 * /unauthorized). Navigation errors (a stale lazy chunk) still reject, as with `router.navigate`.
 */
export async function navigateHandingOff(
  router: Router,
  commands: unknown[],
  extras?: NavigationExtras
): Promise<NavigationOutcome> {
  let handedOff = false;
  const sub = router.events.subscribe(event => {
    if (
      event instanceof NavigationCancel &&
      (event.code === NavigationCancellationCode.SupersededByNewNavigation ||
        event.code === NavigationCancellationCode.Redirect)
    ) {
      handedOff = true;
    }
  });
  try {
    const success = await router.navigate(commands, extras);
    return success ? 'navigated' : handedOff ? 'handedOff' : 'rejected';
  } finally {
    sub.unsubscribe();
  }
}
